import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withDirectives as _withDirectives, vModelText as _vModelText, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "note-metadata-wrapper" }
const _hoisted_2 = {
  key: 0,
  class: "flex p-2 gap-2 items-center border bg-standard-bg border-solid border-standard-border rounded"
}
const _hoisted_3 = ["onKeydown"]
const _hoisted_4 = ["onClick"]

import { computed, nextTick, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { TeamMemberStatus } from '@superthread-com/api';
import constants from '@/utilities/constants';
import CurrentModelMembers from '@/components/widgets/CurrentModelMembers';
import MembersDropdownList from '@/components/widgets/MembersDropdownList';
import DateTimePicker from '@/components/widgets/DateTimePicker';
import ButtonCustom from '@/components/elements/ButtonCustom';
import FilterDropdown from '@/components/widgets/filters/FilterDropdown';
import useNotes from '@/utilities/composables/useNotes';
import useTranslate from '@/utilities/composables/useTranslate';
import { convertTimestampToDate } from '@/utilities/timeDifference';
import ListDropdown from '@/components/widgets/ListDropdown';
import useCustomIcon from '@/utilities/composables/useCustomIcon';
import freeEmailDomains from 'free-email-domains';
import capitalizeFirstLetter from '@/utilities/capitalizeFirstLetter';
import { getCurrentDateInSeconds } from '@/utilities/timeDifference';
import { set, getUnixTime } from 'date-fns';


export default /*@__PURE__*/_defineComponent({
  __name: 'NoteMetadata',
  props: {
  noteId: {
    type: String,
    required: true,
  },
},
  setup(__props) {

const store = useStore();
const { getNoteMetadata, updateNoteMetadataAttendees, updateNote, getNoteMeetingStartTime } =
  useNotes();
const { translate } = useTranslate();
const { iconPath, getCustomIconStyle } = useCustomIcon();

const metadataQuery = ref('');
const oldAttendees = ref([]);
const oldMetadataTime = ref(null);
const timeInput = ref('');
const timeInputRef = ref();
const attendeesPopover = ref();
const datePopover = ref();
const timePopover = ref();
const attendeesDropdown = ref();

const moreOptionItems = [
  {
    id: 'copyEmail',
    label: translate('copyEmail'),
    customIconName: iconPath('mail'),
  },
  {
    id: 'removeAttendee',
    label: translate('removeAttendee'),
    customIconName: iconPath('trash'),
    deleteOrCancelType: true,
  },
];

const metadataPopovers = {
  attendees: attendeesPopover,
  date: datePopover,
  time: timePopover,
};

const showMetadataSection = ref({
  date: false,
  time: false,
  attendees: false,
});

const props = __props;

const noteMetadata = computed(() => getNoteMetadata(props.noteId) || {});

const metadataTime = ref(noteMetadata.value.meetingStartTime);

const attendees = computed(() => noteMetadata.value.meetingAttendees);

const teamMembers = computed(() => {
  return store.getters.getTeamMembers || {};
});

const membersNotInTeam = computed(() => {
  return attendees.value.filter((m) => !m.user_id).map((m) => ({ ...m, id: m.user_id }));
});

const members = computed(() => {
  const existingMembers = Object.values(teamMembers.value).filter(
    (m) => m.status !== TeamMemberStatus.Invited
  );

  /**
   * Members with personal emails are shown first, then members which are
   * grouped by company domain, and existing workspace members are shown last.
   */

  const emailGroups = {
    personal: [],
    company: [],
  };

  membersNotInTeam.value.forEach((m) => {
    if (freeEmailDomains.includes(m.email.split('@')[1]))
      emailGroups.personal.push({ ...m, isPersonalEmail: true });
    else {
      const emailDomain = m.email.split('@')[1];
      const emailGroup = capitalizeFirstLetter(emailDomain.split('.')[0]);
      emailGroups.company.push({ ...m, emailGroup });
    }
  });

  return [...emailGroups.personal, ...emailGroups.company, ...existingMembers]
    .map((m) => ({
      ...m,
      checked: !m.id || attendees.value.some((a) => a.user_id === m.id),
      ...(!m.isPersonalEmail && {
        groupingItem: true,
        groupLabel: m.emailGroup || translate('billing.category.workspaceMembers'),
      }),
    }))
    .sort((a, b) => b.checked - a.checked);
});

const showMetadata = computed(() => {
  return {
    attendees: attendees.value.length > 0 || showMetadataSection.value.attendees,
    date: noteMetadata.value.meetingStartDate || showMetadataSection.value.date,
    time: noteMetadata.value.meetingStartTime || showMetadataSection.value.time,
  };
});

const hasAllMetadata = computed(() => {
  const { attendees, date, time } = showMetadata.value;
  return attendees && date && time;
});

const hasNoMetadata = computed(() => {
  const { attendees, date, time } = showMetadata.value;
  return !attendees && !date && !time;
});

const metadataDropdownItems = computed(() => {
  return [
    ...(!noteMetadata.value.meetingStartDate && !showMetadataSection.value.meetingStartDate
      ? [
          {
            id: 'date',
            label: translate('date'),
            resourceIcon: { src: 'calendar' },
          },
        ]
      : []),
    ...(!noteMetadata.value.meetingStartTime && !showMetadataSection.value.meetingStartTime
      ? [
          {
            id: 'time',
            label: translate('time'),
            resourceIcon: { src: 'clock' },
          },
        ]
      : []),
    ...(attendees.value.length === 0 && !showMetadataSection.value.attendees
      ? [
          {
            id: 'attendees',
            label: translate('attendees'),
            resourceIcon: { src: 'users' },
          },
        ]
      : []),
  ].filter((item) => item.label.toLowerCase().includes(metadataQuery.value.toLowerCase()));
});

const dateMetadataLabel = computed(() =>
  convertTimestampToDate(noteMetadata.value.meetingStartDate)
);

const selectedDate = computed(() => {
  return noteMetadata.value.meetingStartDate || getCurrentDateInSeconds();
});

const timeMetadataLabel = computed(() => getNoteMeetingStartTime(props.noteId));

const handleAttendeeClick = (attendee: any) => {
  updateNoteMetadataAttendees({
    noteId: props.noteId,
    attendee,
    removeAttendee: attendees.value.some((a) => a.user_id === attendee.id),
    local: true,
  });

  focusAttendeesDropdown();
};

const handleUpdateAttendees = () => {
  updateNoteMetadataAttendees({ noteId: props.noteId, oldAttendees: oldAttendees.value });
  oldAttendees.value = attendees.value;
};

const openMetadataDropdown = (id: string) => {
  showMetadataSection.value[id] = true;

  requestAnimationFrame(() => {
    nextTick(() => {
      metadataPopovers[id].value?.show();
    });
  });
};

const setMetadataDate = (time: number, hide?: () => void) => {
  updateNote({
    noteId: props.noteId,
    localField: 'meeting_date',
    fieldToUpdate: 'metadata_date',
    value: time,
  });

  // Hide the date from the shown sections if it has been removed
  if (!time) showMetadataSection.value.date = false;

  hide?.();
};

const setMetadataTime = (hours: number, minutes: number, hide?: () => void) => {
  if (hours === null && minutes === null) {
    metadataTime.value = null;
    showMetadataSection.value.time = false;

    hide?.();
    return;
  }

  metadataTime.value = getUnixTime(
    set(new Date(), { hours, minutes, seconds: 0, milliseconds: 0 })
  );
};

const focusTimeInput = () => {
  nextTick(() => {
    timeInputRef.value?.focus();
  });
};

const handleTimeKeydown = (e: any, hide: () => void) => {
  if (['Escape', 'Enter'].includes(e.key)) hide();
};

const handleTimeChange = (e: any) => {
  const v = e.target.value;
  const [hours, minutes] = v.split(':');
  setMetadataTime(hours, minutes);
};

const handleUpdateTime = () => {
  if (oldMetadataTime.value === metadataTime.value) return;

  updateNote({
    noteId: props.noteId,
    localField: 'meeting_time',
    fieldToUpdate: 'metadata_time',
    value: Math.round(metadataTime.value),
  });
  oldMetadataTime.value = metadataTime.value;
};

const handleMetadataDropdownItemClick = (item: any, hide: () => void) => {
  openMetadataDropdown(item.id);

  const currentDate = new Date();

  // Immediately set the current date or time once clicked
  if (item.id === 'date') setMetadataDate(getCurrentDateInSeconds());
  if (item.id === 'time') setMetadataTime(currentDate.getHours(), currentDate.getMinutes());

  hide();
};

const handleMetadataQueryChange = (query: string) => {
  metadataQuery.value = query;
};

const handleMoreOptionClick = (item: any, member: any, hide?: () => void) => {
  if (item.id === 'copyEmail') {
    navigator.clipboard.writeText(member.email);
    store.dispatch('addUiNotification', {
      message: translate('emailCopiedToClipboard'),
      icon: 'mail',
      duration: 4000,
    });
  }

  if (item.id === 'removeAttendee') handleAttendeeClick(member);

  hide?.();
};

const handleAttendeesClickOutside = (i: any, e: any) => {
  // Prevent closing if nested dropdown items are clicked
  if (e.target.closest('.list-dropdown-scroll')) return;
  i.hide();
};

const focusAttendeesDropdown = () => {
  nextTick(() => {
    attendeesDropdown.value?.$refs?.searchInput?.focus();
  });
};

const handleClickAttendeesPopover = () => {
  const popover = attendeesPopover.value;
  if (popover?.state?.isVisible) popover?.hide();
  else popover?.show();
};

onMounted(() => {
  oldAttendees.value = attendees.value;
  oldMetadataTime.value = noteMetadata.value.meetingStartTime;
  timeInput.value = timeMetadataLabel.value;
});

return (_ctx: any,_cache: any) => {
  const _component_v_popover = _resolveComponent("v-popover")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (showMetadata.value.attendees)
      ? (_openBlock(), _createBlock(_component_v_popover, {
          key: 0,
          ref_key: "attendeesPopover",
          ref: attendeesPopover,
          interactive: "",
          tag: "div",
          "content-tag": "div",
          trigger: 'manual',
          role: 'poper',
          placement: 'bottom-start',
          "on-hide": handleUpdateAttendees,
          "on-click-outside": (i, e) => handleAttendeesClickOutside(i, e),
          "hide-on-click": false,
          onClick: handleClickAttendeesPopover
        }, {
          content: _withCtx(({ state, hide }) => [
            (state.isVisible)
              ? (_openBlock(), _createBlock(_unref(MembersDropdownList), {
                  key: 0,
                  ref_key: "attendeesDropdown",
                  ref: attendeesDropdown,
                  "entity-type-prop": _unref(constants).meetingAttendees,
                  members: members.value,
                  onMemberClick: handleAttendeeClick,
                  onOnMoreOptionsHide: focusAttendeesDropdown,
                  onCloseDropdown: hide
                }, {
                  moreOptions: _withCtx(({ state: moreOptionsState, hide: moreOptionsHide, member }) => [
                    (moreOptionsState.isVisible)
                      ? (_openBlock(), _createBlock(_unref(ListDropdown), {
                          key: 0,
                          items: moreOptionItems,
                          "prevent-blur": true,
                          onClick: (item) => handleMoreOptionClick(item, member, moreOptionsHide),
                          onCloseEscape: moreOptionsHide
                        }, null, 8, ["onClick", "onCloseEscape"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["entity-type-prop", "members", "onCloseDropdown"]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(CurrentModelMembers), {
              class: "pt add-members-wrapper",
              "avatar-size": 'w-5 h-5 text-sm',
              members: attendees.value,
              "always-allow-click": true,
              "tooltip-text": _unref(translate)('editAttendees'),
              onClickWrap: _cache[0] || (_cache[0] = ($event: any) => (openMetadataDropdown('attendees')))
            }, null, 8, ["members", "tooltip-text"]),
            _cache[2] || (_cache[2] = _createTextVNode())
          ]),
          _: 1
        }, 8, ["on-click-outside"]))
      : _createCommentVNode("", true),
    _cache[7] || (_cache[7] = _createTextVNode()),
    (showMetadata.value.date)
      ? (_openBlock(), _createBlock(_component_v_popover, {
          key: 1,
          ref_key: "datePopover",
          ref: datePopover,
          interactive: "",
          tag: "div",
          "content-tag": "div",
          trigger: 'click',
          role: 'poper',
          placement: 'bottom-start'
        }, {
          content: _withCtx(({ state, hide }) => [
            (state.isVisible)
              ? (_openBlock(), _createBlock(_unref(DateTimePicker), {
                  key: 0,
                  "selected-date": selectedDate.value,
                  onDueDate: (date) => setMetadataDate(date, hide),
                  onEsc: hide
                }, null, 8, ["selected-date", "onDueDate", "onEsc"]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _withDirectives(_createVNode(_unref(ButtonCustom), {
              text: dateMetadataLabel.value,
              type: "secondary-regular secondary-button electron-no-drag metadata-button",
              "custom-icon-name": 'calendar',
              "custom-icon-color": '--var(iconDefault)',
              "custom-icon-size": '16px'
            }, null, 8, ["text"]), [
              [_directive_tooltip, {
          content: _unref(translate)('editMeetingDate'),
          placement: 'bottom',
        }]
            ]),
            _cache[3] || (_cache[3] = _createTextVNode())
          ]),
          _: 1
        }, 512))
      : _createCommentVNode("", true),
    _cache[8] || (_cache[8] = _createTextVNode()),
    (showMetadata.value.time)
      ? (_openBlock(), _createBlock(_component_v_popover, {
          key: 2,
          ref_key: "timePopover",
          ref: timePopover,
          interactive: "",
          tag: "div",
          "content-tag": "div",
          trigger: 'click',
          role: 'poper',
          placement: 'bottom-start',
          "on-hide": handleUpdateTime,
          "on-show": focusTimeInput
        }, {
          content: _withCtx(({ state, hide }) => [
            (state.isVisible)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                  _withDirectives(_createElementVNode("input", {
                    ref_key: "timeInputRef",
                    ref: timeInputRef,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((timeInput).value = $event)),
                    type: "time",
                    class: "time-input",
                    onKeydown: (e) => handleTimeKeydown(e, hide),
                    onChange: handleTimeChange
                  }, null, 40, _hoisted_3), [
                    [_vModelText, timeInput.value]
                  ]),
                  _cache[4] || (_cache[4] = _createTextVNode()),
                  _createElementVNode("em", {
                    class: "custom-icons pt delete-icon",
                    style: _normalizeStyle([_unref(getCustomIconStyle)(_unref(iconPath)('trash'))]),
                    onClick: ($event: any) => (setMetadataTime(null, null, hide))
                  }, null, 12, _hoisted_4)
                ]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _withDirectives(_createVNode(_unref(ButtonCustom), {
              text: timeMetadataLabel.value,
              type: "secondary-regular secondary-button electron-no-drag metadata-button",
              "custom-icon-name": 'clock',
              "custom-icon-color": '--var(iconDefault)',
              "custom-icon-size": '16px'
            }, null, 8, ["text"]), [
              [_directive_tooltip, {
          content: _unref(translate)('editMeetingTime'),
          placement: 'bottom',
        }]
            ]),
            _cache[5] || (_cache[5] = _createTextVNode())
          ]),
          _: 1
        }, 512))
      : _createCommentVNode("", true),
    _cache[9] || (_cache[9] = _createTextVNode()),
    (!hasAllMetadata.value)
      ? (_openBlock(), _createBlock(_component_v_popover, {
          key: 3,
          interactive: "",
          tag: "div",
          "content-tag": "div",
          trigger: 'click',
          role: 'poper',
          placement: 'bottom-start',
          "on-hide": () => (metadataQuery.value = '')
        }, {
          content: _withCtx(({ state, hide }) => [
            (state.isVisible)
              ? (_openBlock(), _createBlock(_unref(FilterDropdown), {
                  key: 0,
                  "search-results": metadataDropdownItems.value,
                  placeholder: `${_unref(translate)('meetingDetails')}...`,
                  "on-item-click": (item) => handleMetadataDropdownItemClick(item, hide),
                  "on-item-right-side-click": (item) => handleMetadataDropdownItemClick(item, hide),
                  "select-only-search-result": (item) => handleMetadataDropdownItemClick(item, hide),
                  "hide-filter-popup": hide,
                  onQueryChange: handleMetadataQueryChange,
                  onEsc: hide
                }, null, 8, ["search-results", "placeholder", "on-item-click", "on-item-right-side-click", "select-only-search-result", "hide-filter-popup", "onEsc"]))
              : _createCommentVNode("", true)
          ]),
          default: _withCtx(() => [
            _withDirectives(_createVNode(_unref(ButtonCustom), {
              type: "secondary-regular secondary-button electron-no-drag add-metadata-button pt",
              "custom-icon-name": 'plus',
              "custom-icon-color": '--var(iconDefault)',
              "custom-icon-size": '16px'
            }, null, 512), [
              [_directive_tooltip, {
          content: _unref(translate)('addMeetingDetails'),
          placement: 'bottom',
          onShow: () => hasNoMetadata.value,
        }]
            ]),
            _cache[6] || (_cache[6] = _createTextVNode())
          ]),
          _: 1
        }, 8, ["on-hide"]))
      : _createCommentVNode("", true)
  ]))
}
}

})